/**
 * The colours the board is drawn in.
 *
 * Three of them are the player's: the light squares, the dark squares, and the
 * page behind them. Everything else on the screen is derived, because everything
 * else has to stay *readable* — a number on a dark square, the heading on the
 * page — and asking someone to pick a contrasting ink for every colour they try
 * is asking them to do the work the luminance of the colour they picked already
 * answers.
 *
 * The defaults here and the `:root` block in `lightshadow.css.liquid` are the
 * same palette written twice, and they have to stay that way: the stylesheet is
 * inlined into the shell, so it is what paints a cold start before this module
 * has run, and a mismatch would show as the board changing colour on load.
 */

export interface Palette {
  /** The page behind the board. */
  page: string;
  /** Light squares. */
  light: string;
  /** Shadow squares. */
  shadow: string;
}

export interface Preset {
  id: string;
  name: string;
  palette: Palette;
}

export const PRESETS: readonly Preset[] = [
  {
    // The green and the blue have swapped sides: the light shade is the meadow's
    // grass and the shadow is the water it runs down to, rather than the other
    // way about. The three colours are the originals, exactly — only which square
    // each one paints has changed. Note that leaves the light shade darker than
    // the shadow: nothing in the game asks otherwise, the two shades only have to
    // differ, and every ink here is derived from luminance rather than from the
    // name of the slot.
    id: 'meadow',
    name: 'Meadow',
    palette: { page: '#c8f0dc', light: '#0f7a52', shadow: '#9adcf2' },
  },
  {
    id: 'dusk',
    name: 'Dusk',
    palette: { page: '#dbe4f3', light: '#fbdf8b', shadow: '#333d52' },
  },
  {
    id: 'orchid',
    name: 'Orchid',
    palette: { page: '#ecdff5', light: '#f8c6df', shadow: '#4b2a6b' },
  },
  {
    id: 'slate',
    name: 'Slate',
    palette: { page: '#e5e8ea', light: '#c3cad2', shadow: '#2b3138' },
  },
];

export const DEFAULT_PALETTE: Palette = (PRESETS[0] as Preset).palette;

const HEX = /^#[0-9a-f]{6}$/i;

export function isColor(value: unknown): value is string {
  return typeof value === 'string' && HEX.test(value);
}

export function isPalette(value: unknown): value is Palette {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Partial<Palette>;
  return isColor(candidate.page) && isColor(candidate.light) && isColor(candidate.shadow);
}

export function samePalette(a: Palette, b: Palette): boolean {
  return (
    a.page.toLowerCase() === b.page.toLowerCase() &&
    a.light.toLowerCase() === b.light.toLowerCase() &&
    a.shadow.toLowerCase() === b.shadow.toLowerCase()
  );
}

export function presetFor(palette: Palette): Preset | null {
  return PRESETS.find((preset) => samePalette(preset.palette, palette)) ?? null;
}

function channels(color: string): [number, number, number] {
  const value = Number.parseInt(color.slice(1), 16);
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
}

/** WCAG relative luminance, 0 for black and 1 for white. */
export function luminance(color: string): number {
  const parts = channels(color).map((channel) => {
    const scaled = channel / 255;
    return scaled <= 0.03928 ? scaled / 12.92 : ((scaled + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * parts[0] + 0.7152 * parts[1] + 0.0722 * parts[2];
}

/** The two inks everything on a chosen colour is written in. */
const DARK_INK = '#141922';
const LIGHT_INK = '#ffffff';

/**
 * The luminance at which the two inks are equally readable.
 *
 * Derived rather than picked, from the contrast formula: the ratios to the two
 * inks are equal at sqrt((Ld + .05)(Ll + .05)) - .05, and taking the better ink
 * on each side of that point is what makes the *worst* case as good as it can be
 * — a little over 4:1 for the most awkward mid-tone a colour picker can produce,
 * and comfortably past the 4.5 body-text floor for anything less awkward,
 * including every preset. Note it is nowhere near the middle of the range: the
 * eye is far more sensitive at the dark end, so dark ink wins well below 50%.
 */
const CROSSOVER = Math.sqrt((luminance(DARK_INK) + 0.05) * (luminance(LIGHT_INK) + 0.05)) - 0.05;

/** Ink that can be read on a given colour. */
export function inkFor(color: string): string {
  return luminance(color) > CROSSOVER ? DARK_INK : LIGHT_INK;
}

function rgba(color: string, alpha: number): string {
  const [red, green, blue] = channels(color);
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

/**
 * Paints a palette onto the document.
 *
 * Custom properties on the root element, so every rule in the stylesheet follows
 * without knowing this exists. The `theme-color` meta goes with them: on a phone
 * it is the colour of the browser's own chrome around the page, and leaving it on
 * the default would put a stripe of another palette above the board.
 */
export function applyPalette(palette: Palette): void {
  if (typeof document === 'undefined') return;
  const ink = inkFor(palette.page);
  const style = document.documentElement.style;
  style.setProperty('--ls-page', palette.page);
  style.setProperty('--ls-light', palette.light);
  style.setProperty('--ls-light-ink', inkFor(palette.light));
  style.setProperty('--ls-shadow', palette.shadow);
  style.setProperty('--ls-shadow-ink', inkFor(palette.shadow));
  style.setProperty('--ls-page-ink', ink);
  style.setProperty('--ls-page-muted', rgba(ink, 0.68));
  style.setProperty('--ls-dot', rgba(ink, 0.09));
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', palette.page);
}
